import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { OrganizationInfoCard } from "@/components/organizations/OrganizationInfoCard";
import {
  InviteForm,
  InviteFormSubmissionResult,
} from "@/components/invites/InviteForm";
import { Users, Mail, Clock, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { StepIndicator } from "@/components/onboarding/StepIndicator";

export default function Invite() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  const handleSave = async (result: InviteFormSubmissionResult) => {
    // Only redirect if we have successful entries
    if (result.successful.length > 0) {
      setLocation(
        `/pc-onboarding/processing?count=${result.successful.length}`,
      );
    }
  };

  return (
    <Layout hideSidebar>
      <div className="max-w-7xl mx-auto">
        <StepIndicator currentStep={2} totalSteps={3} />
        <div className="mb-8">
          <h1 className="text-3xl font-bold">{t("pages.pcOnboarding.invite.inviteContractors")}</h1>
          <p className="text-muted-foreground mt-2">
            {t("pages.pcOnboarding.invite.setUpYourContractors")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8">
          {/* Left Column - Welcome & Info */}
          <div className="space-y-6">
            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h2 className="text-2xl font-semibold">{t("pages.pcOnboarding.invite.getStarted")}</h2>
                  </div>

                  <div className="space-y-4 text-muted-foreground">
                    <p className="leading-relaxed">
                      {t("pages.pcOnboarding.invite.inviteYourContractorsTo")}
                    </p>

                    <div className="space-y-3 pt-2">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">
                            {t("pages.pcOnboarding.invite.accountCreation")}
                          </p>
                          <p className="text-sm">
                            {t("pages.pcOnboarding.invite.weLlAutomaticallyCreate")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">
                            {t("pages.pcOnboarding.invite.emailInvitations")}
                          </p>
                          <p className="text-sm">
                            {t("pages.pcOnboarding.invite.contractorsWillReceiveAn")}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">
                            {t("pages.pcOnboarding.invite.processingTime")}
                          </p>
                          <p className="text-sm">
                            {t("pages.pcOnboarding.invite.accountSetupTypicallyTakes")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <OrganizationInfoCard />
          </div>

          {/* Right Column - Form */}
          <div>
            <InviteForm onSave={handleSave} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
